import { FlagCommentsPopover, useAddFlagComment } from '~/entities/document-flags'
import type { Flag } from '~/shared/api/data-ingestion-api/client'
import { Button, Tooltip } from '~/shared/ui'

interface FlagClaimPopoverProps {
  documentKey: string
  persistedFlag: Flag | null
  localFlag: { notes: string } | null
  onCreateFlag: (notes: string) => void
  onUpdateFlag: (notes: string) => void
  onRemoveFlag: () => void
  onResolveFlag: (flagKey: string, resolutionNotes: string) => void
}

export function FlagClaimPopover({
  documentKey,
  persistedFlag,
  localFlag,
  onCreateFlag,
  onUpdateFlag,
  onRemoveFlag,
  onResolveFlag,
}: FlagClaimPopoverProps) {
  const { mutateAsync: addComment, isPending: isAddingComment } = useAddFlagComment()

  const isCreate = localFlag === null && persistedFlag === null
  const isLocal = localFlag !== null && persistedFlag === null

  const trigger = isCreate ? (
    <Button
      size="small"
      materialIcon="flag"
    >
      Flag
    </Button>
  ) : (
    <Tooltip content={persistedFlag ? 'Resolve Flag' : 'Edit Flag'}>
      <span>
        <Button
          size="small"
          materialIcon={{
            children: 'flag',
            fill: true,
          }}
        >
          Flagged
        </Button>
      </span>
    </Tooltip>
  )

  if (persistedFlag) {
    return (
      <FlagCommentsPopover
        mode="persisted"
        flag={persistedFlag}
        canEdit
        busy={isAddingComment}
        trigger={trigger}
        onAddComment={async (text) => {
          await addComment({
            path: { key: documentKey, flagKey: persistedFlag.key },
            body: { notes: text },
          })
        }}
        onResolve={(text) => {
          onResolveFlag(persistedFlag.key, text)
        }}
      />
    )
  }

  if (isLocal) {
    return (
      <FlagCommentsPopover
        mode="local-draft"
        flag={null}
        localDraft={localFlag.notes}
        onSaveDraft={onUpdateFlag}
        onRemoveDraft={onRemoveFlag}
        trigger={trigger}
      />
    )
  }

  return (
    <FlagCommentsPopover
      mode="local-draft"
      flag={null}
      localDraft={null}
      onSaveDraft={onCreateFlag}
      trigger={trigger}
    />
  )
}
